import { useMemo } from 'react';
import { GET_LOCATIONS_QUERY, GetLocationsResponse, GetLocationsVariables, ShopifyLocation } from '../queries';
import { useShopifyGraphQLSWR } from '../useShopifyGraphQLSWR';

interface UseShopifyLocationsOptions {
    /** Page size passed to the `locations` connection. */
    first?: number;
    /** Shopify search-syntax filter, e.g. `name:Warehouse` or `pickup_in_store:enabled`. */
    query?: string;
    /** Include deactivated locations. */
    includeInactive?: boolean;
    /** Include the legacy locations of fulfillment services. */
    includeLegacy?: boolean;
    /** Set to false to skip the request (SWR key becomes null). */
    enabled?: boolean;
}

export const useShopifyLocationsSWR = ({
    first = 50,
    query,
    includeInactive = false,
    includeLegacy = false,
    enabled = true,
}: UseShopifyLocationsOptions = {}) => {
    const variables = useMemo<GetLocationsVariables>(
        () => ({ first, query, includeInactive, includeLegacy }),
        [first, query, includeInactive, includeLegacy]
    );

    const { data, error, isLoading, hasNextPage, hasPrevPage, mutate, refetch, next, prev, reset } =
        useShopifyGraphQLSWR<GetLocationsResponse, GetLocationsVariables>(
            enabled ? 'shopify-locations' : null,
            GET_LOCATIONS_QUERY,
            variables,
            'locations'
        );

    const locations: ShopifyLocation[] = data?.locations?.nodes ?? [];

    return {
        locations,
        error,
        isLoading,
        hasNextPage,
        hasPrevPage,
        mutate,
        refetch,
        next,
        prev,
        reset,
    };
};
